import { createClient } from '@supabase/supabase-js'

const supabaseUrl = process.env.REACT_APP_SUPABASE_URL
const supabaseAnonKey = process.env.REACT_APP_SUPABASE_ANON_KEY

export const supabase = createClient(supabaseUrl, supabaseAnonKey)

// ─── Auth helpers ─────────────────────────────────────────────────────────────
export const signIn = (email, password) =>
  supabase.auth.signInWithPassword({ email, password })

export const signOut = () => supabase.auth.signOut()

export const getSession = () => supabase.auth.getSession()

// ─── Profile helpers ──────────────────────────────────────────────────────────
export const getProfile = async (userId) => {
  const { data, error } = await supabase
    .from('profiles')
    .select('*')
    .eq('id', userId)
    .single()
  return { data, error }
}

export const getAllProfiles = async () => {
  const { data, error } = await supabase
    .from('profiles')
    .select('*')
    .order('created_at', { ascending: false })
  return { data, error }
}

export const updateProfile = async (userId, updates) => {
  const { data, error } = await supabase
    .from('profiles')
    .update(updates)
    .eq('id', userId)
  return { data, error }
}

// ─── Invite helpers ────────────────────────────────────────────────────────────
export const inviteUser = async (email, role = 'staff') => {
  // Uses Supabase admin invite — triggers invitation email
  const { data, error } = await supabase.auth.admin.inviteUserByEmail(email, {
    data: { role }
  })
  return { data, error }
}

export const getPendingInvites = async () => {
  const { data, error } = await supabase
    .from('invites')
    .select('*')
    .order('created_at', { ascending: false })
  return { data, error }
}

// ─── Farm Log helpers ─────────────────────────────────────────────────────────
export const saveFarmLog = async (log) => {
  const { data, error } = await supabase
    .from('farm_logs')
    .upsert(log, { onConflict: 'id' })
    .select()
  return { data, error }
}

export const getFarmLogs = async ({ limit = 50, offset = 0 } = {}) => {
  const { data, error } = await supabase
    .from('farm_logs')
    .select('*, profiles(full_name)')
    .order('log_date', { ascending: false })
    .range(offset, offset + limit - 1)
  return { data, error }
}

export const getFarmLog = async (id) => {
  const { data, error } = await supabase
    .from('farm_logs')
    .select('*')
    .eq('id', id)
    .single()
  return { data, error }
}

// ─── Cash Log helpers ─────────────────────────────────────────────────────────
export const saveCashLog = async (log) => {
  const { data, error } = await supabase
    .from('cash_logs')
    .upsert(log, { onConflict: 'id' })
    .select()
  return { data, error }
}

export const getCashLogs = async ({ limit = 50, offset = 0 } = {}) => {
  const { data, error } = await supabase
    .from('cash_logs')
    .select('*, profiles(full_name)')
    .order('log_date', { ascending: false })
    .range(offset, offset + limit - 1)
  return { data, error }
}

// ─── Invoice helpers ──────────────────────────────────────────────────────────
export const saveInvoice = async (invoice) => {
  const { data, error } = await supabase
    .from('invoices')
    .upsert(invoice, { onConflict: 'id' })
    .select()
  return { data, error }
}

export const getInvoices = async () => {
  const { data, error } = await supabase
    .from('invoices')
    .select('*')
    .order('invoice_date', { ascending: false })
  return { data, error }
}

export const updateInvoiceStatus = async (id, status) => {
  const { data, error } = await supabase
    .from('invoices')
    .update({ status, updated_at: new Date().toISOString() })
    .eq('id', id)
  return { data, error }
}

// ─── Harvest Log helpers ──────────────────────────────────────────────────────
export const saveHarvestLog = async (log) => {
  const { data, error } = await supabase
    .from('harvest_logs')
    .upsert(log, { onConflict: 'id' })
    .select()
  return { data, error }
}

export const getHarvestLogs = async ({ limit = 50 } = {}) => {
  const { data, error } = await supabase
    .from('harvest_logs')
    .select('*, profiles(full_name)')
    .order('harvest_date', { ascending: false })
    .limit(limit)
  return { data, error }
}

// ─── Dashboard stats ──────────────────────────────────────────────────────────
export const getDashboardStats = async () => {
  const [cashLogs, farmLogs, invoices, harvests] = await Promise.all([
    supabase.from('cash_logs').select('total_income, total_expenses, log_date').order('log_date', { ascending: false }).limit(7),
    supabase.from('farm_logs').select('id').limit(1),
    supabase.from('invoices').select('amount, status'),
    supabase.from('harvest_logs').select('grade_a_kg, grade_b_kg, reject_kg, harvest_date').order('harvest_date', { ascending: false }).limit(7)
  ])
  return { cashLogs: cashLogs.data, farmLogs: farmLogs.data, invoices: invoices.data, harvests: harvests.data }
}

// Uganda timezone date helpers — always use these instead of new Date().toISOString()
export const ugandaDate = () => new Date().toLocaleDateString('en-CA', { timeZone: 'Africa/Kampala' })
export const ugandaMonth = () => new Date().toLocaleDateString('en-CA', { timeZone: 'Africa/Kampala' }).slice(0, 7)
export const ugandaDatetime = () => {
  const now = new Date()
  const date = now.toLocaleDateString('en-CA', { timeZone: 'Africa/Kampala' })
  const time = now.toLocaleTimeString('en-UG', { timeZone: 'Africa/Kampala', hour12: false }).slice(0, 5)
  return `${date}T${time}`
}
